import { z } from 'zod';

export type JsonValue = string | number | boolean | null | JsonValue[] | { [k: string]: JsonValue };
export type JsonRecord = Record<string, unknown>;

/** Narrow an API `JsonObject` (generated as `Record<string, never>`) or unknown to a plain record. */
export function asRecord(v: unknown): JsonRecord {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as JsonRecord) : {};
}

export function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

export function prettyJson(v: unknown): string {
  return v === undefined ? '' : JSON.stringify(v, null, 2);
}

export type ParseResult = { ok: true; value: unknown } | { ok: false; error: string };

export function parseJson(text: string): ParseResult {
  if (text.trim() === '') return { ok: true, value: null };
  try {
    return { ok: true, value: JSON.parse(text) as unknown };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'Invalid JSON' };
  }
}

/** zod string that must contain JSON of the given shape kind (object / array / any). */
export function jsonText(kind: 'object' | 'array' | 'any', message: string) {
  return z.string().superRefine((text, ctx) => {
    const r = parseJson(text);
    if (!r.ok) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${message}: ${r.error}` });
      return;
    }
    if (kind === 'object' && r.value !== null && (typeof r.value !== 'object' || Array.isArray(r.value)))
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    if (kind === 'array' && r.value !== null && !Array.isArray(r.value)) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  });
}
